import { memo, useCallback } from "react";

// Servisni sloj
import { FleetDocumentUpload } from "services/api/fleet/vehicles/documents/types";

// UI
import { DatePicker } from "ui/components/DatePicker";
import FileUploadControl from "ui/components/FileUpload/FileUploadControl/FileUploadControl";
import { TextField } from "ui/components/TextField/TextField";

import { DriverDocumentFormProps } from "./types";

/**
 * Korak jedne isprave vozača. Vozačka, osobna i putovnica nose isto — broj, rok i
 * presliku — pa ih crta jedna komponenta, a razlikuje ih opis koraka.
 */
const DriverDocumentForm: React.FC<DriverDocumentFormProps> = (props) => {
  const { step, values, errors, onChange } = props;

  const uploads = values[step.uploadsField];

  const handleTextChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onChange({ ...values, [step.numberField]: e.target.value });
    },
    [values, onChange, step.numberField]
  );

  const handleDateChange = useCallback(
    (_name: string, value: Date | null) => {
      onChange({ ...values, [step.expiryField]: value });
    },
    [values, onChange, step.expiryField]
  );

  const handleUploadSave = useCallback(
    (saved: FleetDocumentUpload[]) => {
      onChange({ ...values, [step.uploadsField]: [...uploads, ...saved] });
    },
    [values, onChange, step.uploadsField, uploads]
  );

  const handleUploadDelete = useCallback(
    (id: string) => {
      onChange({
        ...values,
        [step.uploadsField]: uploads.filter((upload) => upload.id !== id),
      });
    },
    [values, onChange, step.uploadsField, uploads]
  );

  return (
    <div className="flex items-start justify-center px-4 pt-12 pb-6">
      <div className="w-full max-w-4xl">
        <h1 className="text-2xl font-bold text-slate-900 text-center mb-2">
          {step.title}
        </h1>
        <p className="text-center text-md text-slate-500 mb-8">{step.description}</p>

        <div className="border bg-white border-slate-200">
          <div className="border-b border-slate-200 py-6 px-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">
                Podaci isprave
              </h3>
              <p className="text-xs text-slate-500">
                Korak je neobavezan — isprava se može dopuniti kasnije
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <TextField
                  type="text"
                  name={step.numberField}
                  label={step.numberLabel}
                  placeholder={step.numberLabel}
                  value={values[step.numberField]}
                  onChange={handleTextChange}
                  error={errors[step.numberField]}
                  width="full"
                />
              </div>
              <div>
                <DatePicker
                  name={step.expiryField}
                  label={step.expiryLabel}
                  placeholder={step.expiryLabel}
                  value={values[step.expiryField]}
                  onChange={handleDateChange}
                  error={errors[step.expiryField]}
                  width="full"
                />
              </div>
            </div>
          </div>

          <div className="py-6 px-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold">
                Preslika
              </h3>
              <p className="text-xs text-slate-500">Slika ili PDF isprave</p>
            </div>
            <FileUploadControl
              uploads={uploads}
              onUploadSave={handleUploadSave}
              onUploadDelete={handleUploadDelete}
              buttonText="Dodaj presliku"
              modalTitle={`Preslika — ${step.title.toLowerCase()}`}
              emptyText={step.emptyText}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(DriverDocumentForm);
